import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GarnishmentProcessingType } from '@safarilaw-webapp/feature/co-manage/data-access';
import { AccountExemptionType, AccountLocationType, GarnishmentAccount, GarnishmentPayment, GarnishmentStatus, SubjectType } from '@safarilaw-webapp/feature/lpms/data-access';
import { SafariValidators } from '@safarilaw-webapp/shared/ui-kit/safari-form';

import { v4 as uuidv4 } from 'uuid';
export abstract class InfoRequestFormHelper {
  static shouldShowFullname(type: SubjectType, isNew: boolean, changedByUser: boolean) {
    if (type != SubjectType.Individual) {
      return true;
    }
    if (changedByUser && type == SubjectType.Individual) {
      return false;
    }
    return !isNew;
  }
  static setupSubjectNameValidators(subject: FormGroup, changedByUser: boolean) {
    subject.get('name').removeValidators([Validators.required]);
    subject.get('first').removeValidators([Validators.required]);
    subject.get('last').removeValidators([Validators.required]);
    if (InfoRequestFormHelper.shouldShowFullname(+subject.get('type').value, subject.get('isNew').value, changedByUser)) {
      subject.get('name').addValidators([Validators.required]);
    } else {
      subject.get('first').addValidators([Validators.required]);
      subject.get('last').addValidators([Validators.required]);
    }
  }

  static buildAccount(
    item: GarnishmentAccount,
    formBuilder: FormBuilder,
    requireLocationState: boolean,
    accountLocationType: AccountLocationType,
    enableExemptionTrackingFields: boolean,
    processingType: GarnishmentProcessingType,
    garnishmentStatus: GarnishmentStatus,
    isX9: boolean,
    isGarnishmentAutoResponseEnabled: boolean
  ) {
    const locationState = [item ? item.locationState : null, ...(requireLocationState ? [Validators.required] : [])];

    const exemptionType = [enableExemptionTrackingFields && item ? item.exemptionType : null, ...(enableExemptionTrackingFields ? [Validators.required] : [])];

    const exemptionStateLaw = [
      {
        value: enableExemptionTrackingFields && item ? item.exemptionStateLaw : null,
        disabled: !(item?.exemptionType == AccountExemptionType.FederalAndState || item?.exemptionType == AccountExemptionType.State)
      },
      ...(enableExemptionTrackingFields && (item?.exemptionType == AccountExemptionType.FederalAndState || item?.exemptionType == AccountExemptionType.State) ? [Validators.required] : [])
    ];

    const exemptionNotes = [
      enableExemptionTrackingFields && item ? item.exemptionNotes : null,
      ...(enableExemptionTrackingFields && item?.exemptionType && item.exemptionType != AccountExemptionType.None ? [Validators.required] : [])
    ];
    const exemptionAmount = [
      { value: enableExemptionTrackingFields ? item?.exemptionAmount : null, disabled: false },
      enableExemptionTrackingFields && item?.exemptionType && item?.exemptionType != AccountExemptionType.None ? [Validators.required] : []
    ];
    // When the garnishment is X9 and status is not SeizeAssets, some fields get disabled
    const isX9Disabled = isX9 && garnishmentStatus != GarnishmentStatus.SeizeAssets;
    const seizedAmountDisabled = (isX9 || isGarnishmentAutoResponseEnabled) && garnishmentStatus != GarnishmentStatus.SeizeAssets;
    // For non-standard always default to 0 for these fields. They are all calculated via ledger transactions
    const seizedAmount = [
      { value: processingType == GarnishmentProcessingType.Standard ? (item ? item.seizedAmount : 0) : 0, disabled: seizedAmountDisabled },
      seizedAmountDisabled ? [] : [Validators.required]
    ];
    const number = [item ? item.number : null, [Validators.required]];
    const processingFee = [{ value: processingType == GarnishmentProcessingType.Standard ? undefined : 0, disabled: isX9Disabled }, []];
    const releasedAmount = [{ value: processingType == GarnishmentProcessingType.Standard ? undefined : 0, disabled: isX9Disabled }, []];

    const account = {
      uuid: uuidv4(),
      __isNew: undefined,
      number,
      notes: item ? [item.notes] : [null],
      type: item ? [item.type] : [null],
      // For non-standard always default to 0 for these fields. They are all calculated via ledger transactions
      seizedAmount,

      processingFee,
      releasedAmount,
      locationState,
      exemptionType,
      exemptionStateLaw,
      exemptionNotes,
      enableAutomatedTransfer: item ? [item.enableAutomatedTransfer] : [false],
      accountBalance: item ? [item.accountBalance] : [null],
      exemptionAmount,
      offsetAmount: item ? [item.offsetAmount] : [null],
      // UI only - Not an API value
      releaseAmount: null,
      // UI only - Not an API value
      showReleaseAmount: false
    };
    const formGroup = formBuilder.group(account);
    formGroup.get('releaseAmount').addValidators([SafariValidators.currencyLessThanOrEqual(formGroup.get('seizedAmount'), 'Release Amount must be less than or equal to Seized Amount')]);
    return formGroup;
  }
  static buildPayment(item: GarnishmentPayment, formBuilder: FormBuilder) {
    const msg = 'Amount must be greater than $0';
    const payment = {
      uuid: uuidv4(),
      accountNumber: item ? [item.accountNumber, Validators.required] : [null],
      checkNumber: item ? [item.checkNumber, Validators.required] : [null, Validators.required],
      paymentAmount: item ? [item.paymentAmount.toFixed(2), [Validators.required, SafariValidators.currencyGreaterThanZero(msg)]] : [null, Validators.required],
      paymentDate: item ? [item.paymentDate, Validators.required] : [null, Validators.required]
    };
    return formBuilder.group(payment);
  }
}
